import { useEffect, useState, useMemo } from 'react'
import { usePageMeta } from '../../hooks/usePageMeta'
import api from '../../services/api'
import { getImageUrl } from '../../utils/helpers'
import GalleryGrid from './components/GalleryGrid'
import CategoryFilter from './components/CategoryFilter'
import Lightbox from './components/Lightbox'
import './Gallery.css'

interface GalleryCategory {
  id: number
  name: string
  slug: string
  media_count: number
}

interface GalleryItem {
  id: number
  file_name: string
  file_path: string
  mime_type: string
  title: string
  description: string | null
  url: string
  is_video: boolean
  category_id: number | null
  category_name: string | null
  category_slug: string | null
}

interface YoutubeVideo {
  id: number
  title: string
  description: string | null
  video_id: string
  thumbnail_url: string
  embed_url: string
  category_id: number | null
  category_name?: string | null
  category_slug?: string | null
}

// Unified type for combined gallery display
export interface UnifiedGalleryItem {
  id: string
  type: 'media' | 'youtube'
  title: string
  description: string | null
  thumbnail_url: string
  category_id: number | null
  category_name: string | null
  file_path?: string
  is_video?: boolean
  video_id?: string
  embed_url?: string
}

function Gallery() {
  usePageMeta({
    title: 'Galerie',
    description: 'Découvrez notre campagne en images et vidéos.',
  })

  const [mediaItems, setMediaItems] = useState<GalleryItem[]>([])
  const [youtubeVideos, setYoutubeVideos] = useState<YoutubeVideo[]>([])
  const [categories, setCategories] = useState<GalleryCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState('all')
  const [lightboxItem, setLightboxItem] = useState<UnifiedGalleryItem | null>(null)

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    fetchGallery()
  }, [activeCategory])

  const fetchCategories = async () => {
    try {
      const data = await api.get<GalleryCategory[]>('/gallery/categories')
      setCategories(data)
    } catch (err) {
      console.error('Failed to fetch categories:', err)
    }
  }

  const fetchGallery = async () => {
    try {
      setLoading(true)
      setError(null)
      const categoryParam = activeCategory !== 'all' ? `?category=${activeCategory}` : ''

      // Fetch both media and YouTube videos in parallel
      const [media, youtube] = await Promise.all([
        api.get<GalleryItem[]>(`/gallery${categoryParam}`),
        api.get<YoutubeVideo[]>(`/youtube-videos/published${categoryParam}`),
      ])

      setMediaItems(media)
      setYoutubeVideos(youtube)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement de la galerie')
    } finally {
      setLoading(false)
    }
  }

  // Merge media items and YouTube videos into unified format
  const unifiedItems = useMemo((): UnifiedGalleryItem[] => {
    const items: UnifiedGalleryItem[] = []

    // Add media items
    mediaItems.forEach((item) => {
      items.push({
        id: `media-${item.id}`,
        type: 'media',
        title: item.title || item.file_name,
        description: item.description,
        thumbnail_url: getImageUrl(item.file_path),
        category_id: item.category_id,
        category_name: item.category_name,
        file_path: item.file_path,
        is_video: item.is_video,
      })
    })

    // Add YouTube videos
    youtubeVideos.forEach((video) => {
      items.push({
        id: `youtube-${video.id}`,
        type: 'youtube',
        title: video.title,
        description: video.description,
        thumbnail_url: video.thumbnail_url,
        category_id: video.category_id,
        category_name: video.category_name || null,
        video_id: video.video_id,
        embed_url: video.embed_url,
      })
    })

    return items
  }, [mediaItems, youtubeVideos])

  const handleItemClick = (item: UnifiedGalleryItem) => {
    setLightboxItem(item)
  }

  const handleCloseLightbox = () => {
    setLightboxItem(null)
  }

  if (loading && unifiedItems.length === 0) {
    return (
      <div className="gallery-container">
        <div className="gallery-header">
          <h1>Galerie</h1>
          <p>Decouvrez notre campagne en images et videos</p>
        </div>
        <div className="gallery-loading">
          <div className="spinner"></div>
          <p>Chargement de la galerie...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="gallery-container">
        <div className="gallery-header">
          <h1>Galerie</h1>
          <p>Decouvrez notre campagne en images et videos</p>
        </div>
        <div className="gallery-error">
          <p>Une erreur est survenue lors du chargement de la galerie.</p>
          <p className="error-message">{error}</p>
          <button onClick={fetchGallery} className="retry-button">
            Reessayer
          </button>
        </div>
      </div>
    )
  }

  if (unifiedItems.length === 0) {
    return (
      <div className="gallery-container">
        <div className="gallery-header">
          <h1>Galerie</h1>
          <p>Decouvrez notre campagne en images et videos</p>
        </div>
        <div className="gallery-empty">
          <p>Aucun media n'est disponible pour le moment.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="gallery-container">
      <div className="gallery-header">
        <h1>Galerie</h1>
        <p>Decouvrez notre campagne en images et videos</p>
      </div>

      <CategoryFilter
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />

      <GalleryGrid items={unifiedItems} onItemClick={handleItemClick} />

      {lightboxItem && (
        <Lightbox item={lightboxItem} onClose={handleCloseLightbox} />
      )}
    </div>
  )
}

export default Gallery
